import type { UsersTranslations } from "../interfaces/users";

export const usersTranslations: UsersTranslations = {
  page_title: "Usuarios",
  page_subtitle: "Personas con acceso a la plataforma, en todas las organizaciones.",
  page_subtitle_single_org: "Personas con acceso a la plataforma en tu organización.",
  action_refresh: "Actualizar",
  action_create: "Crear usuario",

  stat_no_roles: "personas sin ningún rol asignado. Pueden iniciar sesión y no ver nada.",
  stat_no_roles_zero: "Todas las personas tienen al menos un rol asignado.",

  tile_group_label: "Filtro por estado",
  tile_total: "Total",
  tile_active: "Activos",
  tile_unverified: "Sin verificar",
  tile_suspended: "Suspendidos",
  tile_inactive: "Desactivados",
  tile_other_status: "{{count}} en otro estado",

  search_placeholder: "Buscar por nombre o correo",
  search_min_hint: "Escribe al menos 3 caracteres para buscar. Los demás filtros siguen aplicando.",
  filter_role_label: "Rol",
  filter_role_all: "Todos los roles",
  filter_role_unavailable: "No se pudo cargar el catálogo de roles, así que no se puede filtrar por rol.",

  col_person: "Persona",
  col_organization: "Organización",
  col_access: "Acceso",
  col_status: "Estado",
  col_account_access: "Acceso a la cuenta",
  col_actions: "Acciones",
  badge_you: "Tú",
  badge_coloca: "Coloca",
  row_actions_label: "Acciones para {{name}}",

  status_active: "Activo",
  status_inactive: "Desactivado",
  status_suspended: "Suspendido",
  status_pending_verification: "Sin verificar",

  signin_suspended: "Suspendido",
  signin_inactive: "Desactivado",
  signin_locked: "Bloqueado · {{minutes}} min",
  signin_unverified: "Correo sin verificar",
  signin_never: "Nunca ingresó",
  signin_failing: "{{count}} intentos fallidos",
  // `count` es la variable reservada de i18next para el plural: sin esta forma, el valor más frecuente
  // distinto de cero renderiza "1 intentos fallidos". Se interpola {{count}} en vez de escribir "1" porque
  // en portugués la forma `one` cubre 0 y 1, y un literal imprimiría "1" para cero.
  signin_failing_one: "{{count}} intento fallido",
  signin_suspended_title: "Todo inicio de sesión es rechazado mientras esté suspendido.",
  signin_inactive_title: "La cuenta está desactivada y no puede iniciar sesión.",
  signin_locked_title: "El bloqueo se levanta solo. Nada lo acorta.",
  signin_unverified_title: "El correo no está verificado.",
  signin_never_title: "La persona nunca ha iniciado sesión.",
  signin_failing_title: "Intentos fallidos acumulados. A los 5 la cuenta se bloquea 30 minutos.",
  signin_ok_title: "Último acceso: {{date}}",

  org_none: "Sin organización",
  org_unresolved: "no resuelto",
  org_internal: "interna",
  org_directory_failed:
    "No se pudieron cargar los nombres de las organizaciones. Los datos de los usuarios no están afectados.",
  org_directory_truncated:
    "Hay más organizaciones de las que se pudieron precargar. Los nombres que falten se resuelven al abrirse.",
  org_directory_loading: "Cargando organizaciones…",
  org_directory_empty: "No hay organizaciones para seleccionar.",

  access_none: "Sin roles",
  access_unavailable: "Roles no disponibles",
  access_tally: "{{allowed}}/{{total}} permisos",
  access_extra: "+{{count}}",

  action_view: "Ver y editar",
  action_manage_access: "Gestionar accesos",
  action_verify_email: "Verificar correo",
  action_activate: "Activar",
  action_deactivate: "Desactivar",
  action_suspend: "Suspender",
  action_set_password: "Establecer contraseña",
  action_delete: "Eliminar",
  action_cancel: "Cancelar",

  empty_title: "Todavía no hay usuarios",
  empty_body: "Crea la primera persona con acceso a la plataforma.",
  empty_filtered_title: "Ningún usuario coincide con los filtros",
  empty_filtered_body: "Ajusta la búsqueda, el estado o el rol para volver a ver la población completa.",
  empty_clear_filters: "Limpiar filtros",
  error_load_failed: "No se pudo cargar la lista de usuarios.",
  error_retry: "Reintentar",

  create_title: "Crear usuario",
  create_subtitle: "Se crea como miembro de una organización.",
  create_field_full_name: "Nombre completo",
  create_field_email: "Correo",
  create_field_organization: "Organización",
  create_field_organization_placeholder: "Busca la organización",
  create_field_language: "Idioma",
  create_field_country: "País",
  create_field_phone: "Teléfono",
  create_field_phone_hint: "Con código de país y sin el símbolo +. Ejemplo: 573101234567.",
  create_section_credentials: "Credenciales de acceso",
  create_field_password: "Contraseña",
  create_generate: "Generar",
  create_note_must_change: "Se le pedirá cambiar esta contraseña al iniciar sesión por primera vez.",
  create_note_no_email: "No se envía ningún correo — entrégale la contraseña por un canal seguro.",
  create_submit: "Crear usuario",
  create_success: "Usuario creado",

  detail_section_data: "Datos",
  detail_section_access: "Acceso",
  detail_section_password: "Contraseña",
  detail_section_activity: "Actividad",
  detail_email_warning:
    "Cambiar el correo borra la verificación: la persona vuelve a «Correo sin verificar».",
  detail_phone_hint: "Déjalo vacío para eliminarlo.",
  detail_org_label: "Organización",
  detail_save: "Guardar cambios",
  detail_close: "Cerrar",
  detail_saved: "Cambios guardados",
  detail_alert_locked:
    "El bloqueo se levanta solo en {{minutes}} minutos. Nada lo acorta: cambiar la contraseña no deja entrar a la persona.",
  detail_alert_two_factor:
    "Esta cuenta puede tener 2FA activo sin dispositivo inscrito. Conviene revisarlo: establecer una contraseña nueva no lo resuelve.",
  detail_access_summary_none: "Sin roles asignados. Puede iniciar sesión y no ver nada.",
  detail_access_summary_unavailable:
    "No se pudieron leer los roles de esta persona. No es lo mismo que no tener ninguno.",
  detail_access_summary: "{{count}} roles asignados · {{allowed}}/{{total}} permisos efectivos",
  detail_access_summary_one: "{{count}} rol asignado · {{allowed}}/{{total}} permisos efectivos",
  detail_manage_access: "Gestionar accesos",

  activity_created: "Creado",
  activity_updated: "Última actualización",
  activity_last_login: "Último acceso",
  activity_password_changed: "Contraseña cambiada",
  activity_email_verified: "Correo verificado",
  activity_failed_attempts: "Intentos fallidos",
  activity_locked_until: "Bloqueado hasta",
  activity_two_factor_flag: "2FA (indicador)",
  activity_never: "Nunca",
  activity_yes: "Sí",
  activity_no: "No",

  password_new: "Nueva contraseña",
  password_confirm: "Confirmar",
  password_requirements: "Entre 8 y 128 caracteres, con mayúscula, minúscula, número y símbolo.",
  password_mismatch: "Las contraseñas no coinciden.",
  password_note_sessions: "Esto no cierra las sesiones abiertas de la persona.",
  password_note_no_force: "No se le pedirá cambiarla en el próximo inicio de sesión.",
  password_note_no_email: "No se envía ningún correo.",
  password_note_compromised:
    "Si la cuenta está comprometida: suspende primero, después cambia la contraseña.",
  password_submit: "Establecer contraseña",
  password_success: "Contraseña actualizada",

  suspend_title: "¿Suspender a {{name}}?",
  suspend_body:
    "No se cierran las sesiones que ya tenga abiertas, pero cualquier inicio de sesión futuro será rechazado.",
  suspend_confirm: "Suspender",
  delete_title: "¿Eliminar a {{name}}?",
  delete_body:
    "Se elimina de forma reversible solo por ingeniería, y todas sus sesiones se cierran de inmediato.",
  delete_email_note: "Su correo queda ocupado: no podrás volver a crear una persona con esa dirección.",
  delete_confirm: "Eliminar",
  delete_success: "Usuario eliminado",

  verify_email_success: "Correo verificado",
  status_change_success: "Estado actualizado",
  error_email_taken: "Ya existe un usuario con este correo.",
  error_email_deleted_reuse:
    "No se pudo crear. Si este correo perteneció a un usuario eliminado, no se puede reutilizar: prueba con otro.",
  validation_required: "Este campo es obligatorio.",
  validation_email: "Escribe un correo válido.",
};
